import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProduct, HasProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import CustomEnvelopeIcon from "@tCentro/Icons/CustomEnvelopeIcon";
import CustomInfoIcon from "@tCentro/Icons/CustomInfoIcon";
import CustomTruckIcon from "@tCentro/Icons/CustomTruckIcon";
import WikaLogo from "@tCentro/Icons/WikaLogo";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    getFasterModalIsOpen?: boolean;
    setGetFasterModalIsOpen: (value: boolean) => void;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    isCartPage: getCurrentPage(state).type === "CartPage",
    cart: getCurrentCartState(state).value,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = HasProduct & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    infoIconStyles?: IconPresentationProps;
    infoIconStylesWeight?: IconPresentationProps;
    infoLinkStyles?: InjectableCss;
    outOfStockContainerStyles?: InjectableCss;
    requireDrawingApproval?: InjectableCss;
    requireDrawingApprovalWrapper?: InjectableCss;
    drawwingApprovalIcon?: IconPresentationProps;
    envelopeIconStyles?: IconPresentationProps;
    zoneStyles?: InjectableCss;
    getFasterLinkStyles?: InjectableCss;
    quickShipWrapper?: InjectableCss;
    quickShipContentWrapper?: InjectableCss;
    wikaheading?: TypographyPresentationProps;
    wikaSubheading?: TypographyPresentationProps;
    quickShipLogoWrap?: InjectableCss;
    tbdFreightMessage?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    infoIconStylesWeight: {
        size: 15,
        css: css`
            margin-top: 3px;
            svg {
                > path {
                    fill: ${getColor("text.link")};
                }
            }
        `,
    },
    tbdFreightMessage: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            color: ${getColor("text.link")};
            display: flex;
            align-items: start;
            gap: 5px;
            margin: 2px 0 0;
            margin-bottom: 6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        max-width: 307px !important;
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 1198px) {
                min-width: 277px;
            }
        `,
    },
    zoneStyles: {
        css: css``,
    },
    getFasterLinkStyles: {
        css: css`
            font-family: Poppins;
            font-size: 12px;
            font-weight: 600;
            line-height: 16px;
            text-align: left;
        `,
    },
    envelopeIconStyles: {
        color: "#0F96D9",
        css: css`
            margin-right: 0px !important;

            > svg {
                width: 12px;
                height: 9px;
            }
        `,
    },
    outOfStockContainerStyles: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: 4px;
            width: 100%;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            span[data-test-selector^="availabilityMessage"] {
                font-size: 12px;
                font-style: normal;
                color: ${getColor("text.main")};
                font-weight: 600;
                line-height: 16px;
            }
        `,
    },
    availabilityByWarehouseLinkGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    infoIconStyles: {
        size: 12,
        css: css`
            svg {
                > path {
                    fill: #807f80;
                }
            }
        `,
    },
    infoLinkStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 12px;
            margin-left: 4px;
            color: #9e9d9e;
        `,
    },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    requireDrawingApprovalWrapper: {
        css: css`
            flex: 1 1 auto;
            max-width: 290px !important;
            width: 100% !important;
            display: flex;
            flex-direction: row;
            color: #3ca865;
            padding-top: 8px;
        `,
    },
    drawwingApprovalIcon: {
        size: 12,
        css: css`
            > svg {
                > path {
                    fill: #3ca865;
                }
            }
        `,
    },
    requireDrawingApproval: {
        css: css`
            font-weight: 600;
            font-size: 12px;
            line-height: 16px;
            color: #3ca865;
            margin-left: 2px;
        `,
    },
    quickShipWrapper: {
        css: css`
            display: grid;
            align-items: center;
            padding: 5px;
            border-radius: 5px;
            grid-template-columns: 8% 89%;
            background-color: #d8f0e2;
            width: 100%;
            gap: 10px;
            padding: 8px;
            > span {
                width: auto;
                height: 100%;
                > svg {
                    width: 100%;
                    height: 100%;
                }
            }
        `,
    },
    quickShipContentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 5px;
        `,
    },
    wikaheading: {
        css: css`
            font-family: Poppins;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 1;
        `,
    },
    wikaSubheading: {
        css: css`
            font-family: Poppins;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 1;
        `,
    },
    quickShipLogoWrap: {
        css: css`
            display: flex;
            align-items: center;
            gap: 5px;
            > span {
                width: 44px;
                height: 100%;
                > svg {
                    width: 100%;
                    height: 100%;
                }
            }
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 5px;
    border-radius: 5px;
    width: 100%;
    &.outOfStock {
        background-color: #328ebe33;
        div {
            & > span:nth-child(1) {
                color: #328ebe;
            }
        }
    }
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    ${IconWrapper} {
        margin-right: 10px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;

    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "success";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return Check;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    extendedStyles,
    showInventory,
    product,
    isCartPage,
    cart,
    setGetFasterModalIsOpen,
    getFasterModalIsOpen,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const modalgetFasterModalCloseHandler = () => {
        setGetFasterModalIsOpen(false);
    };

    const openGetFasterModalHandler = () => {
        setGetFasterModalIsOpen(true);
    };

    const [isRequiredDrawingApproval, setIsRequiredDrawingApproval] = useState(false);
    useEffect(() => {
        if (product?.properties?.requiredDrawingApproval === "true") {
            setIsRequiredDrawingApproval(true);
        }
    }, []);
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);

    const className = availability?.messageType === 2 ? "outOfStock" : "";

    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }
    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    const isCustomAvailabilityMsg = product?.properties?.isCustomAvailabilityMsg;

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            <InventoryMessage className={className} color={color} {...styles.inventoryMessage}>
                <StyledWrapper
                    css={css`
                        display: flex;
                        align-items: center;
                        width: 100%;
                    `}
                >
                    <Icon color={color} src={iconSrc} />
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                        {availability.message.includes("\n")
                            ? availability.message.split("\n").map((line, idx) => (
                                  <span key={idx} style={{ display: "block", fontWeight: idx === 0 ? 600 : 400 }}>
                                      {line}
                                  </span>
                              ))
                            : availability.message}
                    </Typography>
                </StyledWrapper>
                {isProductDetailsPage && availability.messageType === 2 && isCustomAvailabilityMsg === "true" && (
                    <StyledWrapper onClick={openGetFasterModalHandler}>
                        {/* Will display when custom availability msg is  present */}
                        <Link href="" {...styles.getFasterLinkStyles}>
                            <Icon src={CustomEnvelopeIcon} {...styles.envelopeIconStyles} />
                            {siteMessage("Pdp_outOfStockTypicallyShipsWithin")}
                        </Link>
                    </StyledWrapper>
                )}
                {isProductDetailsPage && availability.messageType === 2 && isCustomAvailabilityMsg === "false" && (
                    <StyledWrapper onClick={openGetFasterModalHandler}>
                        {/* Will display when custom availability msg is not present */}
                        <Link href="" {...styles.getFasterLinkStyles}>
                            <Icon src={CustomEnvelopeIcon} {...styles.envelopeIconStyles} />
                            {siteMessage("Pdp_outOfStockAvailableforPurchase")}
                        </Link>
                    </StyledWrapper>
                )}
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    const quickShipComponent = (
        <StyledWrapper {...styles.quickShipWrapper}>
            <Icon
                src={CustomTruckIcon}
                css={css`
                    max-width: 24px;
                `}
            />
            <StyledWrapper {...styles.quickShipContentWrapper}>
                <StyledWrapper {...styles.quickShipLogoWrap} data-test-selector="wikaQuickShip">
                    <Icon
                        src={WikaLogo}
                        css={css`
                            max-width: 40px;
                        `}
                    />
                    <Typography as="p" {...styles.wikaheading}>
                        {translate("Quick Ship")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography as="p" {...styles.wikaSubheading}>
                        {translate("Ships in")} <strong style={{ fontWeight: 600 }}>{translate("24 hours")}</strong>{" "}
                        {translate("- order by 2pm EDT")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );

    const isQuickShip =
        product?.properties?.quickShip?.toLowerCase() === "yes" && product.brand?.name?.toLowerCase() === "wika";

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{isQuickShip ? quickShipComponent : inventoryTextComponent}</GridItem>
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}

            <StyledWrapper {...styles.outOfStockContainerStyles}>
                <Icon
                    style={{
                        visibility: !isQuickShip && availability?.messageType === 2 ? "visible" : "hidden",
                    }}
                    src={CustomInfoIcon}
                    {...styles.infoIconStyles}
                ></Icon>
                <Typography
                    as="p"
                    {...styles.infoLinkStyles}
                    style={{
                        visibility: !isQuickShip && availability?.messageType === 2 ? "visible" : "hidden",
                    }}
                >
                    {translate("Subject to factory lead times")}
                </Typography>
            </StyledWrapper>
            {isProductDetailsPage && isRequiredDrawingApproval && (
                <GridItem {...styles.requireDrawingApprovalWrapper}>
                    <Icon src={CustomInfoIcon} {...styles.drawwingApprovalIcon}></Icon>
                    <Typography {...styles.requireDrawingApproval}>
                        {siteMessage("requiredDrwaingApprovalPdp")}
                    </Typography>
                </GridItem>
            )}
            {cart?.cartLines?.map((getProp: any, index: number) => {
                if (
                    getProp.properties.requiredDrawingApproval === "true" &&
                    isCartPage &&
                    getProp.productId === productId
                ) {
                    return (
                        <GridItem {...styles.requireDrawingApprovalWrapper} key={index}>
                            <Icon src={CustomInfoIcon} {...styles.drawwingApprovalIcon}></Icon>
                            <Typography {...styles.requireDrawingApproval}>
                                {siteMessage("requiredDrawingApprovalCart")}
                            </Typography>
                        </GridItem>
                    );
                }
                return null;
            })}
            {cart?.cartLines?.map((getProp: any, index: number) => {
                if (getProp.properties.isItemOverWeight === "true" && isCartPage && getProp.productId === productId) {
                    return (
                        <>
                            <Typography as="p" {...styles.tbdFreightMessage}>
                                <Icon src={CustomInfoIcon} {...styles.infoIconStylesWeight}></Icon>
                                {siteMessage("Shipping_Item_OverWeight")}
                            </Typography>
                        </>
                    );
                }
                return null;
            })}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductAvailability));
